import { Link } from "react-router-dom";
import { CheckCircle2, Phone, ArrowRight, FileText, Scale, Clock, Shield } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { ResourceHero } from "@/components/site/ResourceHero";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { ReviewedBy } from "@/components/site/ReviewedBy";
import { CTASection } from "@/components/site/CTASection";
import { brand, founder } from "@/data/site/brand";
import { stockImages } from "@/data/site/stockImages";

const lastReviewed = "2026-06-30";
const datePublished = "2026-06-30";

const QUICK_ANSWER =
  "The 'IRS Fresh Start Program' isn't a single program — it's the consumer name for a set of IRS collection-relief options expanded in 2011–2012: streamlined Installment Agreements (now up to $50,000 owed), expanded Offer in Compromise eligibility, Currently Not Collectible (CNC) status, and easier federal tax lien withdrawal. Most taxpayers who owe under $50,000 and are current on filings can qualify for a streamlined IA online in minutes; OIC and CNC require Form 433 financial disclosure and IRS approval. There is no application called the 'Fresh Start Program,' and no firm can guarantee acceptance.";

const pillars = [
  {
    icon: FileText,
    title: "Streamlined Installment Agreement",
    forms: "Form 9465 or IRS Online Payment Agreement",
    eligibility: "Owe ≤ $50,000 (combined tax + penalties + interest), all returns filed, can pay within 72 months or the CSED — whichever is shorter.",
    fee: "$22–$225 IRS setup fee (waived/reduced for low-income).",
    body: "The Fresh Start expansion raised the streamlined threshold from $25,000 to $50,000 and the term from 60 to 72 months. No Form 433 financial disclosure is required if you stay under the cap. Direct-debit IAs avoid most lien filings and carry the cheapest setup fee.",
  },
  {
    icon: Scale,
    title: "Offer in Compromise (OIC)",
    forms: "Form 656 + Form 433-A (OIC) or 433-B (OIC)",
    eligibility: "IRS must agree your Reasonable Collection Potential (RCP) is less than the full balance. RCP = realizable equity in assets + future income over 12 or 24 months.",
    fee: "$205 application fee + 20% lump-sum offer (both waived under low-income certification).",
    body: "Fresh Start liberalized the OIC formula — the IRS now counts 12 months of future income for lump-sum offers (down from 48) and 24 months for periodic offers (down from 60). Acceptance rates are roughly 30–40% nationally; the right cases settle for cents on the dollar, the wrong cases waste $205 and a year.",
  },
  {
    icon: Shield,
    title: "Currently Not Collectible (CNC / Status 53)",
    forms: "Form 433-A or 433-F",
    eligibility: "Allowable monthly expenses (using IRS Collection Financial Standards) meet or exceed monthly income — paying the IRS would create economic hardship.",
    fee: "No IRS fee.",
    body: "CNC freezes IRS collection: no levies, no garnishments, no installment payments. Penalties and interest still accrue and the IRS reviews your status periodically. CNC is often the right answer for fixed-income retirees, disability-only households, and anyone in a temporary hardship — and it's usually faster to get than an OIC.",
  },
  {
    icon: Clock,
    title: "Federal Tax Lien Relief",
    forms: "Form 12277 (withdrawal) / Form 14135 (discharge) / Form 14134 (subordination)",
    eligibility: "Lien withdrawal: balance ≤ $25,000 and on a direct-debit IA, or the lien was filed in error. Discharge/subordination: needed to sell or refinance specific property.",
    fee: "No IRS fee.",
    body: "Fresh Start made lien withdrawal easier for taxpayers on a direct-debit IA. Withdrawal removes the public Notice of Federal Tax Lien from the record — a major credit-score win — even though the underlying liability still exists until paid.",
  },
];

const eligibilityChecklist = [
  "All required tax returns are filed (or can be filed quickly).",
  "Current-year estimated tax payments and federal tax deposits are up to date.",
  "You can document your income and expenses with paystubs, bank statements, and bills.",
  "No active bankruptcy case (bankruptcy stays IRS collection separately).",
  "You haven't already defaulted on a prior IA without good cause in the last 12 months.",
];

const myths = [
  {
    myth: "“Settle your IRS debt for pennies on the dollar — guaranteed!”",
    truth: "No legitimate firm can guarantee an OIC. The IRS accepts roughly 30–40% of submitted offers. Acceptance depends on your Reasonable Collection Potential — not on any firm's negotiation skill.",
  },
  {
    myth: "“The Fresh Start Program is ending — call now!”",
    truth: "The Fresh Start initiative is permanent IRS policy, not a limited-time program. Anyone using artificial urgency is a sales-floor red flag.",
  },
  {
    myth: "“If I owe under $10,000, I qualify automatically.”",
    truth: "There is no automatic qualification. You still need to be filed and current on payments, and your installment-agreement terms still depend on what you can afford within the IRS Collection Statute Expiration Date.",
  },
  {
    myth: "“I have to hire a tax attorney.”",
    truth: "Most Fresh Start work is financial analysis and IRS negotiation — exactly what CPAs and Enrolled Agents do every day. Attorneys are required only for Tax Court litigation, criminal exposure, or complex asset protection issues.",
  },
];

const howCpaHelps = [
  "Pull your IRS account, wage & income, and return transcripts to verify what you actually owe — most taxpayers' balances are wrong by thousands.",
  "Run the IRS Reasonable Collection Potential formula before you submit anything, so we know whether an OIC, IA, or CNC is the right path.",
  "Prepare Form 433-A/433-F with IRS Collection Financial Standards correctly applied (most rejections are over the standards, not the substance).",
  "File the Form 2848 Power of Attorney so the IRS calls us — not you — and so we can stop active levies or garnishments within hours.",
  "Track the Collection Statute Expiration Date (CSED) so we don't extend it accidentally with the wrong filing.",
];

const IRSFreshStartProgramGuide = () => {
  const jsonLd: object[] = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "IRS Fresh Start Program: The 2026 Plain-English Guide",
      description: QUICK_ANSWER.slice(0, 200),
      datePublished,
      dateModified: lastReviewed,
      author: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      reviewedBy: {
        "@type": "Person",
        name: founder.name,
        jobTitle: founder.title,
        url: `${brand.siteUrl}/about/franklin-sofi`,
      },
      publisher: {
        "@type": "Organization",
        name: brand.legalName,
        url: brand.siteUrl,
      },
      mainEntityOfPage: `${brand.siteUrl}/resources/irs-fresh-start-program-guide`,
      inLanguage: "en-US",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: [".speakable", "h1", "h2"],
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is the IRS Fresh Start Program?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The IRS Fresh Start Program is the consumer name for a 2011–2012 expansion of IRS collection-relief options: streamlined Installment Agreements up to $50,000, expanded Offer in Compromise rules, Currently Not Collectible status, and easier federal tax lien withdrawal. It is permanent IRS policy, not a limited-time offer.",
          },
        },
        {
          "@type": "Question",
          name: "Who qualifies for the IRS Fresh Start Program?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Eligibility depends on which Fresh Start option you use. Streamlined Installment Agreement: owe $50,000 or less and be current on filings. Offer in Compromise: your Reasonable Collection Potential must be less than the balance. Currently Not Collectible: monthly income at or below allowable IRS standards. There is no single application.",
          },
        },
        {
          "@type": "Question",
          name: "How much does the IRS Fresh Start Program cost?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "IRS fees are minimal: $22–$225 for an Installment Agreement, $205 plus 20% lump-sum for an Offer in Compromise (both waived under low-income certification), and nothing for Currently Not Collectible. Professional fees for CPA representation typically run $750–$8,500 depending on the option used and case complexity.",
          },
        },
        {
          "@type": "Question",
          name: "How long does the IRS Fresh Start Program take?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Streamlined Installment Agreements approve in minutes via the IRS Online Payment Agreement portal. Currently Not Collectible status typically takes 30–90 days once Form 433 is submitted. Offer in Compromise typically takes 6–12 months for an initial decision and may be appealed if rejected.",
          },
        },
        {
          "@type": "Question",
          name: "Can the IRS Fresh Start Program remove a tax lien?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes — Fresh Start made it easier to withdraw a Notice of Federal Tax Lien. If you owe $25,000 or less and enter a direct-debit Installment Agreement, you can request withdrawal on Form 12277 after three on-time payments. Withdrawal removes the lien from the public record even though the underlying tax debt remains.",
          },
        },
      ],
    },
  ];

  return (
    <SiteLayout>
      <PageSEO
        title="IRS Fresh Start Program: 2026 Guide From a Licensed CPA"
        description="What the IRS Fresh Start Program actually is, who qualifies, and how installment agreements, Offers in Compromise, and Currently Not Collectible status work — explained by a CPA."
        path="/resources/irs-fresh-start-program-guide"
        image={stockImages.countingCash.src}
        ogType="article"
        author={founder.name}
        publishedTime={datePublished}
        modifiedTime={lastReviewed}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "IRS Fresh Start Program Guide", href: "/resources/irs-fresh-start-program-guide" },
        ]}
        jsonLd={jsonLd}
        speakableSelectors={[".speakable", ".faq-question", ".faq-answer"]}
      />

      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "IRS Fresh Start Program" },
        ]}
        eyebrow="Tax relief programs"
        title="The IRS Fresh Start Program, explained honestly"
        summary="It's not a single program — it's four collection-relief tools the IRS expanded so taxpayers can settle, pay over time, or pause collections. Here's how each one actually works and who really qualifies."
        facts={[
          { label: "Last reviewed", value: "Jun 2026" },
          { label: "Reviewed by", value: founder.name },
          { label: "Read time", value: "9 min" },
        ]}
      />

      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 max-w-4xl">
          <QuickAnswer
            heading="What is the IRS Fresh Start Program?"
            answer={QUICK_ANSWER}
          />

          <ReviewedBy date={lastReviewed} />

          {/* Pillars */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-12 mb-2">
            The four tools inside Fresh Start
          </h2>
          <p className="text-[15px] text-navy-dark/70 mb-6">
            Each tool solves a different problem. The right one depends on what you owe, what you make, and what you own.
          </p>
          <div className="space-y-5">
            {pillars.map((p) => (
              <div key={p.title} className="rounded-2xl border border-border bg-white p-6 lg:p-8">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-secondary p-3 flex-shrink-0">
                    <p.icon className="w-6 h-6 text-navy-dark" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-heading text-xl font-bold text-navy-dark m-0">{p.title}</h3>
                    <p className="text-[13px] text-navy-dark/60 m-0 mt-1">{p.forms}</p>
                  </div>
                </div>
                <p className="text-[15px] text-navy-dark/85 leading-relaxed mt-4">{p.body}</p>
                <dl className="mt-4 grid sm:grid-cols-2 gap-3 text-[14px]">
                  <div className="rounded-lg bg-secondary/60 p-3">
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-navy-dark/60">Eligibility</dt>
                    <dd className="m-0 mt-1 text-navy-dark/85">{p.eligibility}</dd>
                  </div>
                  <div className="rounded-lg bg-secondary/60 p-3">
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-navy-dark/60">IRS fee</dt>
                    <dd className="m-0 mt-1 text-navy-dark/85">{p.fee}</dd>
                  </div>
                </dl>
              </div>
            ))}
          </div>

          {/* Eligibility */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            Before any Fresh Start option will work
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mb-4">
            The IRS will not consider a Fresh Start resolution unless these baseline requirements are met:
          </p>
          <ul className="space-y-2 text-[15px] text-navy-dark/85">
            {eligibilityChecklist.map((item) => (
              <li key={item} className="flex gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Myths */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            Fresh Start myths sales-floors keep selling
          </h2>
          <div className="space-y-4">
            {myths.map((m) => (
              <div key={m.myth} className="rounded-xl border border-border bg-white p-5">
                <p className="m-0 font-semibold text-navy-dark">{m.myth}</p>
                <p className="m-0 mt-1.5 text-[15px] text-navy-dark/85 leading-relaxed">
                  <strong className="text-gold-dark">Reality: </strong>{m.truth}
                </p>
              </div>
            ))}
          </div>

          {/* CPA help */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            How a CPA firm helps you navigate Fresh Start
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed mb-4">
            You can apply for any Fresh Start option yourself. What a credentialed firm adds is diagnosis, leverage, and protection:
          </p>
          <ul className="space-y-2 text-[15px] text-navy-dark/85">
            {howCpaHelps.map((item) => (
              <li key={item} className="flex gap-2.5">
                <CheckCircle2 className="w-4 h-4 text-gold-dark mt-1 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>

          {/* Bottom line */}
          <h2 className="font-heading text-3xl font-bold text-navy-dark mt-16 mb-4">
            The bottom line
          </h2>
          <p className="text-[16px] text-navy-dark/85 leading-relaxed">
            The Fresh Start Program isn't a magic eraser — it's four well-defined IRS procedures that, used correctly, can settle a tax debt, freeze collections, or remove a lien. Used incorrectly, they waste a year and leave you worse off. The first step is always the same: pull the transcripts and run the numbers honestly. We'll do that for free.
          </p>

          {/* CTA grid */}
          <div className="mt-10 grid sm:grid-cols-2 gap-4">
            <Link to="/services/irs-collections/offer-in-compromise" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">Offer in Compromise help</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
            <Link to="/resources/how-much-does-tax-resolution-cost" className="group flex items-center justify-between rounded-xl border border-border bg-white p-5 hover:border-gold hover:shadow-md transition">
              <span className="font-semibold text-navy-dark">Tax resolution pricing</span>
              <ArrowRight className="w-4 h-4 text-gold-dark group-hover:translate-x-1 transition" />
            </Link>
          </div>

          <div className="mt-8 rounded-xl bg-navy-dark text-white p-6 lg:p-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gold mb-1">
                Free Fresh Start eligibility review
              </p>
              <p className="m-0 text-white/90">
                We'll pull your IRS transcripts and tell you which Fresh Start option actually fits your case.
              </p>
            </div>
            <a
              href={`tel:${brand.phoneTel}`}
              className="inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-light text-navy-dark font-bold px-6 py-3 rounded-lg whitespace-nowrap"
            >
              <Phone className="w-4 h-4" /> {brand.phone}
            </a>
          </div>
        </div>
      </section>

      <CTASection />
    </SiteLayout>
  );
};

export default IRSFreshStartProgramGuide;
